import { useRef, useState } from "react";
import styled from "styled-components";
import theme from "../../style/theme";
import CircleCloseFillIcon from "../icons/CircleCloseFillIcon";
import ExpandLessIcon from "../icons/ExpandLessIcon";
import ExpandMoreIcon from "../icons/ExpandMoreIcon";
import SearchLineIcon from "../icons/SearchLineIcon/SearchLineIcon";
import { useClickOutside } from "../../utils/click/handle-click";

interface Props {
  placeholder?: string;
  onChangeTextField?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClickSelectItem: (key: string) => void;
  resetTextField?: () => void;
  value?: string;
  candidateMap: { [key: string]: string };
  width?: string;
  isError?: boolean;
  isActive?: boolean;
  disabled?: boolean;
  label: string;
}

export const LabelAutocomplete: React.FC<Props> = (
  {
    placeholder,
    value,
    onChangeTextField,
    onClickSelectItem,
    resetTextField,
    candidateMap,
    width,
    isError,
    isActive,
    disabled,
    label,
  }
) => {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const menuListRef = useRef<HTMLDivElement>(null);

  const scrollToItem = (index: number) => {
    const menuList = menuListRef.current;
    const menuItem = menuList?.children[index] as HTMLElement;

    if (menuList && menuItem) {
      const menuListRect = menuList.getBoundingClientRect();
      const menuItemRect = menuItem.getBoundingClientRect();

      if (menuItemRect.bottom > menuListRect.bottom) {
        menuList.scrollTop += menuItemRect.bottom - menuListRect.bottom;
      } else if (menuItemRect.top < menuListRect.top) {
        menuList.scrollTop -= menuListRect.top - menuItemRect.top;
      }
    }
  };

  const handleClickBox = () => {
    setOpen(true);
    textFieldRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const candidates = Object.keys(candidateMap);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev < candidates.length - 1 ? prev + 1 : prev;
          scrollToItem(newIndex);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : prev;
          scrollToItem(newIndex);
          return newIndex;
        });
        break;
      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        const matchingKey = candidates.find(key => candidateMap[key] === value);

        if (value && matchingKey) {
          onClickSelectItem(matchingKey);
          setOpen(false);
          setSelectedIndex(-1);
        } else if (candidates.length === 1) {
          onClickSelectItem(candidates[0]);
          setOpen(false);
          setSelectedIndex(-1);
        } else if (selectedIndex >= 0 && candidates[selectedIndex]) {
          onClickSelectItem(candidates[selectedIndex]);
          setOpen(false);
          setSelectedIndex(-1);
        }
        break;
      case 'Escape':
        setOpen(false);
        setSelectedIndex(-1);
        resetTextField?.();
        break;
    }
  };

  useClickOutside(ref, () => setOpen(false));

  return (
    <div ref={ref}>
      <SelectedItem
        width={width}
        onClick={handleClickBox}
        isError={isError}
        isActive={isActive}
      >
        <LeftSelectedItemIconWrapper>
          <SearchLineIcon
            fill={
              isActive
                ? theme.palette.primitives.yellow400
                : theme.palette.primitives.gray400
            }
          />
        </LeftSelectedItemIconWrapper>
        {isActive ? (
          <TextLabel
            ref={textFieldRef}
            placeholder={placeholder}
            width={width}
            onChange={onChangeTextField}
            onKeyDown={handleKeyDown}
          >{`${label} ${value}`}</TextLabel>
        ) : (
          <TextField
            ref={textFieldRef}
            placeholder={placeholder}
            width={width}
            value={value}
            onChange={onChangeTextField}
            onKeyDown={handleKeyDown}
          />
        )}
        <RightSelectedItemWrapper>
          <CircleCloseFillIconWrapper
            onClick={resetTextField}
            style={{ visibility: value ? "visible" : "hidden" }}
          >
            <CircleCloseFillIcon />
          </CircleCloseFillIconWrapper>
          <Divider />
          <SelectedIconWrapper>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </SelectedIconWrapper>
        </RightSelectedItemWrapper>
      </SelectedItem>
      {open && Object.keys(candidateMap).length > 0 && (
        <MenuItemList ref={menuListRef} width={width}>
          {Object.keys(candidateMap).map((candidate, index) => {
            const isSelected = value === candidate || index === selectedIndex;
            return (
              <MenuItem
                key={candidate}
                selected={isSelected}
                onClick={() => {
                  onClickSelectItem(candidate);
                  setOpen(false);
                  setSelectedIndex(-1);
                }}
              >
                <MenuItemLabel>{candidateMap[candidate]}</MenuItemLabel>
              </MenuItem>
            );
          })}
        </MenuItemList>
      )}
    </div>
  );
};

interface SelectedItemProps {
  width?: string;
  isError?: boolean;
  isActive?: boolean;
}

const SelectedItem = styled.div<SelectedItemProps>`
  width: ${(props) => props.width || "14.25rem"};
  height: 42px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  border: 1px solid ${(props) => {
    if (props.isError) {
      return theme.palette.primitives.red500;
    }
    if (props.isActive) {
      return theme.palette.primitives.yellow400;
    }
    return theme.palette.primitives.gray300;
  }};

  background-color: ${(props) =>
    props.isActive
      ? theme.palette.primitives.yellow50
      : theme.palette.primitives.white};

  &:hover {
    background-color: #f5f6f9;
  }
`;

interface TextFieldProps {
  width?: string;
  isError?: boolean;
}

const TextField = styled.input<TextFieldProps>`
  width: 76%;
  height: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  border: none;
  background-color: transparent;

  &:focus {
    outline: none;
  }

  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.palette.primitives.gray900};
`;

interface TextLabelProps {
  width?: string;
  isError?: boolean;
}

const TextLabel = styled.div<TextLabelProps>`
  width: 76%;
  height: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  border: none;
  background-color: transparent;

  &:focus {
    outline: none;
  }

  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.palette.primitives.gray900};
`;

const SelectedItemLabel = styled.div`
  height: 2.625rem;
  margin-left: 0.75rem;
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.palette.primitives.gray900};
  display: flex;
  align-items: center;
`;

const LeftSelectedItemIconWrapper = styled.div`
  width: 36px;
  height: 24px;
  box-sizing: border-box;
  padding: 0px 4px 0px 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RightSelectedItemWrapper = styled.div`
  // width: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Divider = styled.div`
  width: 1px;
  height: 26px;
  background: ${theme.palette.primitives.gray200};
`;

const SelectedIconWrapper = styled.div`
  display: flex;
  padding: 0 9px;
  justify-content: center;
  align-items: center;
`;

const CircleCloseFillIconWrapper = styled.div`
  padding: 0 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

interface MenuItemListProps {
  width?: string;
}

const MenuItemList = styled.div<MenuItemListProps>`
  width: ${(props) => props.width || "14.25rem"};
  max-height: 12.188rem;
  padding: 6.5px 0;
  position: absolute;
  background-color: #ffffff;
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0px 6px 8px rgba(0, 0, 0, 0.2);
  border: solid 1px #bec3cb;
  z-index: 500;
  // max-height: calc(100vh - 400px);
  overflow-y: scroll;
  overflow-x: hidden;

  /* 스크롤 바 스타일 */

  &::-webkit-scrollbar {
    width: 5px;
    background-color: #ffffff;
  }

  /* 스크롤바 색상 */

  &::-webkit-scrollbar-thumb {
    background-color: #1c2026;
    border-radius: 10px;
  }

  /* 스크롤바가 마우스 오버될 때 색상 */

  &::-webkit-scrollbar-thumb:hover {
    background-color: #bfbfbf;
  }
`;

interface MenuItemProps {
  selected: boolean;
}

const MenuItem = styled.div<MenuItemProps>`
  padding-left: 12px;
  height: 36px;
  display: flex;
  color: #3f444e;
  align-items: center;
  background-color: ${(props) => (props.selected ? "#FFF7D9" : "#ffffff")};

  &:hover {
    background-color: #f5f6f9;
  }

  font-size: 14px;
  font-weight: 500;
`;

const LeftMenuItemIconWrapper = styled.div`
  width: 42px;
  height: 36px;
  box-sizing: border-box;
  padding: 9px 8px 9px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MenuItemLabel = styled.div`
  height: 2.5rem;
  font-style: normal;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${theme.palette.primitives.black};
  display: flex;
  align-items: center;
`;
